import { Link } from "react-router-dom";
import type { ProposalResourceRole, ProposedRevision } from "../api/types";
import { Button } from "../compose/controls";
import { Check, Repeat2, X } from "../compose/Icon";
import { formatDate } from "../lib/format";
import { diffStats, lineDiff } from "../lib/linediff";

/**
 * ProposalCard — one agent-proposed revision in its teal "machine" coat
 * (design-system §1.3 · §0 signature 5: "machine actions wear a different coat").
 * Agent authorship is unmistakable — a dashed teal border + teal author chip +
 * teal wash — so a curator never confuses an AI proposal with an approved edit.
 * The before/after is an honest line diff of `base_body` → `proposed_body`.
 *
 * Pure + prop-driven (no data fetching), so the accept/reject surface is unit-
 * testable in isolation. Accept/Reject render only for a PENDING proposal when
 * the viewer may decide (the human editor+ gate, invariant 7); a reviewer sees
 * the same card WITHOUT the buttons.
 */

const STATUS_LABEL: Record<ProposedRevision["status"], string> = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const STATUS_CLASS: Record<ProposedRevision["status"], string> = {
  pending: "badge--agent",
  accepted: "badge--delivered",
  rejected: "badge--superseded",
};

/** The human-readable resource-slot label for the article-unit context chip. */
const ROLE_LABEL: Record<Exclude<ProposalResourceRole, "">, string> = {
  brief: "Brief",
  research: "Research",
  article: "Article",
};

export function ProposalCard({
  proposal,
  canDecide,
  slug,
  busy = false,
  onAccept,
  onReject,
}: {
  proposal: ProposedRevision;
  canDecide: boolean;
  /** The workspace slug — enables the article-unit workbench deep link. When
   *  absent (or the proposal is loose) the header shows the bare document title. */
  slug?: string;
  busy?: boolean;
  onAccept?: (p: ProposedRevision) => void;
  onReject?: (p: ProposedRevision) => void;
}) {
  const p = proposal;
  const diff = lineDiff(p.base_body, p.proposed_body);
  const stats = diffStats(diff);
  const pending = p.status === "pending";

  // Article-unit context (design §3): a proposal that revises a resource of a
  // live article unit links straight into that pane of the workbench. A loose/
  // legacy proposal (no unit) keeps the bare document title, unchanged.
  const role: ProposalResourceRole = p.resource_role ?? "";
  const unitScoped = p.article_unit_id != null && Boolean(slug);
  const roleLabel = role !== "" ? ROLE_LABEL[role] : "Resource";
  const showRole = role !== "" ? role : "article";

  return (
    <article
      className="proposal-card"
      data-testid={`proposal-card-${p.id}`}
      data-status={p.status}
    >
      <header className="proposal-card__head">
        <span
          className="badge badge--agent proposal-card__agent"
          data-testid={`proposal-agent-${p.id}`}
        >
          <Repeat2 size={13} strokeWidth={1.5} />
          {p.author_agent}
        </span>
        <div className="proposal-card__where">
          {unitScoped ? (
            <Link
              to={`/w/${slug}/article/${p.article_unit_id}?show=${showRole}`}
              className="proposal-card__unit"
              data-testid={`proposal-unit-${p.id}`}
              title={`Open the ${roleLabel.toLowerCase()} of “${p.unit_title ?? ""}” in the workbench`}
            >
              <span className="role-chip role-chip--on">{roleLabel}</span>
              <span className="proposal-card__unit-title">{p.unit_title}</span>
            </Link>
          ) : (
            <span className="proposal-card__doc">{p.document_title}</span>
          )}
          <span className="dot">·</span>
          <span className="proposal-card__block">block {p.block_ref}</span>
          <span className="dot">·</span>
          <span>{formatDate(p.created_at)}</span>
        </div>
        <span className={`badge ${STATUS_CLASS[p.status]} proposal-card__status`}>
          {STATUS_LABEL[p.status]}
        </span>
      </header>

      {p.rationale ? (
        <p className="proposal-card__rationale" data-testid={`proposal-rationale-${p.id}`}>
          {p.rationale}
        </p>
      ) : null}

      <div
        className="proposal-diff"
        data-testid={`proposal-diff-${p.id}`}
        aria-label={`Proposed change: ${stats.added} added, ${stats.removed} removed lines`}
      >
        {diff.map((line, i) => (
          <div
            key={i}
            className={`proposal-diff__line proposal-diff__line--${line.type}`}
          >
            <span className="proposal-diff__gutter" aria-hidden>
              {line.type === "add" ? "+" : line.type === "remove" ? "−" : " "}
            </span>
            <span className="proposal-diff__text">{line.text || " "}</span>
          </div>
        ))}
      </div>

      <footer className="proposal-card__foot">
        <span className="proposal-diff__stats">
          <span className="proposal-diff__stat proposal-diff__stat--add">
            +{stats.added}
          </span>
          <span className="proposal-diff__stat proposal-diff__stat--remove">
            −{stats.removed}
          </span>
        </span>

        {pending && canDecide ? (
          <div className="proposal-card__actions">
            <Button
              variant="ghost"
              disabled={busy}
              data-testid={`reject-proposal-${p.id}`}
              icon={<X size={15} strokeWidth={1.5} />}
              onClick={() => onReject?.(p)}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              data-testid={`accept-proposal-${p.id}`}
              icon={<Check size={15} strokeWidth={1.5} />}
              onClick={() => onAccept?.(p)}
            >
              Accept
            </Button>
          </div>
        ) : !pending && p.decided_by ? (
          <span
            className="proposal-card__decided"
            data-testid={`proposal-decided-${p.id}`}
          >
            {STATUS_LABEL[p.status]} by {p.decided_by}
            {p.decided_at ? ` · ${formatDate(p.decided_at)}` : ""}
          </span>
        ) : null}
      </footer>
    </article>
  );
}
